import React, { useState, useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
ChartJS.register(ArcElement, Tooltip, Legend);

// Default deliveries data
const DEFAULT_DATA = {
  homeDeliveries: 528,
  institutional: 286468,
  cSection: 45492,
};

// Dummy function to generate district-level deliveries based on seed
function generateDistrictDeliveries(district, seed = 0) {
  // simple deterministic random generator
  const hash = [...district].reduce((acc, char) => acc + char.charCodeAt(0), seed);
  const total = 50000 + (hash % 50000);
  const home = Math.round(total * (0.01 + ((hash % 20) / 100)));
  const institutional = total - home;
  const cSection = Math.round(institutional * (0.1 + ((hash % 10) / 100)));
  return { homeDeliveries: home, institutional, cSection };
}

export default function DeliveriesData({ data = DEFAULT_DATA, selectedDistrict = null, seed = 0 }) {
  const [open, setOpen] = useState(false);

  // Recompute data when district or seed changes
  const effectiveData = useMemo(() => {
    if (!selectedDistrict) return data;
    return generateDistrictDeliveries(selectedDistrict, seed);
  }, [data, selectedDistrict, seed]);

  const totalDeliveries = effectiveData.homeDeliveries + effectiveData.institutional;
  const homePerc = ((effectiveData.homeDeliveries / totalDeliveries) * 100).toFixed(2);
  const institutionalPerc = ((effectiveData.institutional / totalDeliveries) * 100).toFixed(2);
  const cSectionPerc = ((effectiveData.cSection / effectiveData.institutional) * 100).toFixed(2);

  const chartData = {
    labels: ["Home Deliveries", "Institutional Deliveries"],
    datasets: [
      {
        data: [effectiveData.homeDeliveries, effectiveData.institutional],
        backgroundColor: ["#f97316", "#5e72e4"],
        borderWidth: 1,
      },
    ],
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-3 px-3 py-2 rounded-lg shadow-sm bg-white border hover:shadow-md w-full"
      >
        <div
          className="w-10 h-10 flex items-center justify-center rounded-md"
          style={{ background: "#fff7ed" }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C13.1046 2 14 2.89543 14 4C14 5.10457 13.1046 6 12 6C10.8954 6 10 5.10457 10 4C10 2.89543 10.8954 2 12 2ZM6 22C6 18.6863 8.68629 16 12 16C15.3137 16 18 18.6863 18 22H6Z"
              fill="#f97316"
            />
          </svg>
        </div>

        <div className="text-left">
          <div className="text-xs text-slate-500">Total Deliveries</div>
          <div className="text-lg font-semibold">{totalDeliveries.toLocaleString()}</div>
        </div>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
          />
          <aside className="relative ml-auto w-full max-w-2xl h-full bg-white shadow-2xl overflow-auto p-4">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold">Deliveries Data</h3>
                <p className="text-xs text-slate-500">Source: HMIS Apr24 - Mar25</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="px-3 py-1 rounded bg-slate-100 text-sm"
              >
                Close
              </button>
            </div>

            <div className="bg-white p-4 rounded-lg border flex flex-col items-center mb-4">
              <h5 className="text-sm font-medium mb-2 text-center">Home vs Institutional Deliveries</h5>
              <div style={{ width: "100%", maxWidth: 300, height: 350 }}>
                <Doughnut data={chartData} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg border flex flex-col items-center">
                <div className="text-sm text-slate-500 mb-1">Total C-Section Deliveries</div>
                <div className="text-2xl font-bold">{effectiveData.cSection.toLocaleString()}</div>
                <div className="text-xs text-slate-400 mt-1">{cSectionPerc}% of Institutional Deliveries</div>
              </div>

              <div className="bg-white p-4 rounded-lg border flex flex-col items-center">
                <div className="text-sm text-slate-500 mb-1">Home Deliveries Attended by SBA</div>
                <div className="text-2xl font-bold">{effectiveData.homeDeliveries.toLocaleString()}</div>
                <div className="text-xs text-slate-400 mt-1">{homePerc}% of Total Deliveries</div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
